import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Target, 
  Users, 
  FileText, 
  Lock, 
  ArrowLeft,
  CheckCircle,
  Building2,
  Globe,
  Award
} from 'lucide-react';
import LevelUpHeader from '../components/LevelUpHeader';
import LevelUpFooter from '../components/LevelUpFooter';

/**
 * About Page - Explains what "Why Microsoft" is
 * Accessible from footer, provides overview for partners
 */
function About({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
      <LevelUpHeader user={user} onLogout={onLogout} />

      {/* Hero Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1B5E5F 0%, #0D4A4B 100%)',
        padding: '60px 24px',
        color: '#FFFFFF'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(196, 229, 56, 0.2)',
            padding: '8px 16px',
            borderRadius: '20px',
            marginBottom: '24px'
          }}>
            <Shield size={18} style={{ color: '#C4E538' }} aria-hidden="true" />
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#C4E538' }}>Partner Resource Portal</span>
          </div>
          
          <h1 style={{ 
            fontSize: '42px', 
            fontWeight: '700', 
            marginBottom: '20px',
            lineHeight: '1.2',
            color: '#FFFFFF'
          }}>
            About Why Microsoft – Compete Enablement for Partners
          </h1>
          
          <p style={{ 
            fontSize: '18px', 
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.7',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            Your exclusive gateway to competitive intelligence and sales enablement resources 
            designed specifically for Microsoft Partners.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: '60px 24px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          
          {/* What is "Why Microsoft" */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '20px'
            }}>
              What is "Why Microsoft"?
            </h2>
            
            <div style={{ 
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '28px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              lineHeight: '1.8'
            }}>
              <p style={{ fontSize: '16px', color: '#4B5563', marginBottom: '16px' }}>
                <strong>"Why Microsoft"</strong> is a secure, partner-exclusive platform hosted on the 
                LevelUp platform by Technofocus. It provides authorized Microsoft Partners with access to 
                confidential competitive intelligence materials, battle cards, sales presentations, and 
                enablement content.
              </p>
              
              <p style={{ fontSize: '16px', color: '#4B5563', marginBottom: '16px' }}>
                This portal serves as a centralized repository where partners can find curated resources 
                to effectively position Microsoft solutions against competitors in the market. Whether 
                you're in sales, presales, or technical roles, you'll find relevant materials to support 
                your customer conversations.
              </p>
              
              <p style={{ fontSize: '16px', color: '#4B5563', margin: 0 }}>
                Access is restricted to verified Microsoft Partners who have accepted the required 
                Non-Disclosure Agreement (NDA), ensuring the confidentiality of the materials shared 
                within this platform.
              </p>
            </div>
          </section>

          {/* Key Features */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '20px'
            }}>
              Key Features
            </h2>
            
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {[
                {
                  icon: Target,
                  title: 'Competitive Battle Cards',
                  description: 'Access detailed competitive analysis and positioning guides for major competitors including AWS, Google Cloud, and others.'
                },
                {
                  icon: FileText,
                  title: 'Sales Enablement Content',
                  description: 'Download presentations, solution guides, and customer-ready materials to accelerate your sales cycles.'
                },
                {
                  icon: Globe,
                  title: 'Multi-Language Support',
                  description: 'Find content in multiple languages to support your regional markets and diverse customer base.'
                },
                {
                  icon: Lock,
                  title: 'Secure & Confidential',
                  description: 'All content is protected under NDA. Access is verified and tracked for compliance purposes.'
                }
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div 
                    key={index}
                    style={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '12px',
                      padding: '24px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      border: '1px solid #E5E7EB'
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '10px',
                      backgroundColor: '#E0F2F1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px'
                    }}>
                      <Icon size={24} style={{ color: '#1B5E5F' }} />
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937', marginBottom: '8px' }}>
                      {feature.title}
                    </h3>
                    <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6', margin: 0 }}>
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Solution Areas */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '20px'
            }}>
              Content Coverage
            </h2>
            
            <div style={{ 
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '28px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}>
              <p style={{ fontSize: '16px', color: '#4B5563', marginBottom: '20px' }}>
                Our content library covers key Microsoft solution areas:
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                {[
                  { area: 'AI Business Solutions (ABS)', plays: ['AI Business Process', 'AI Workforce'] },
                  { area: 'Azure - Cloud & AI Platform', plays: ['Innovate with Azure AI Apps', 'Migrate & Modernize', 'Unify Your Data Platform'] },
                  { area: 'Security', plays: ['Data Security', 'Modern SecOps'] }
                ].map((solution, index) => (
                  <div 
                    key={index}
                    style={{
                      backgroundColor: '#F9FAFB',
                      borderRadius: '8px',
                      padding: '16px',
                      border: '1px solid #E5E7EB'
                    }}
                  >
                    <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1B5E5F', marginBottom: '10px' }}>
                      {solution.area}
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {solution.plays.map((play, i) => (
                        <li key={i} style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                          {play}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Content Ownership Notice */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '20px'
            }}>
              Content Ownership & Governance
            </h2>
            
            <div style={{ 
              backgroundColor: '#FEF3C7',
              borderRadius: '12px',
              padding: '28px',
              border: '1px solid #F59E0B'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  backgroundColor: '#FDE68A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Building2 size={20} style={{ color: '#92400E' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#92400E', marginBottom: '12px' }}>
                    Important Notice
                  </h3>
                  <p style={{ fontSize: '15px', color: '#92400E', lineHeight: '1.7', marginBottom: '12px' }}>
                    All competitive intelligence content, battle cards, presentations, and materials 
                    available on <strong>"Why Microsoft"</strong> are <strong>owned by Microsoft Corporation</strong> and 
                    are provided directly by authorized Microsoft representatives.
                  </p>
                  <p style={{ fontSize: '15px', color: '#92400E', lineHeight: '1.7', margin: 0 }}>
                    Technofocus Pte Ltd operates the LevelUp platform solely as a <strong>hosting and 
                    technical service provider</strong>. Technofocus does not create, review, validate, 
                    approve, or govern the content uploaded to this portal. All responsibility for 
                    the accuracy, compliance, and appropriateness of the content remains with 
                    Microsoft and the designated content owners.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Who Can Access */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '20px'
            }}>
              Who Can Access?
            </h2>
            
            <div style={{ 
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '28px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  'Authorized Microsoft Partners with valid partner credentials',
                  'Users who have signed in via the LevelUp platform',
                  'Individuals who have read and accepted the NDA agreement',
                  'Sales, Presales, and Technical professionals within partner organizations'
                ].map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <CheckCircle size={20} style={{ color: '#22C55E', flexShrink: 0 }} />
                    <span style={{ fontSize: '15px', color: '#4B5563' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Platform Information */}
          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ 
              fontSize: '28px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '20px'
            }}>
              Platform Information
            </h2>
            
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px'
            }}>
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                textAlign: 'center'
              }}>
                <Award size={32} style={{ color: '#1B5E5F', marginBottom: '12px' }} />
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                  Operated By
                </h4>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                  Technofocus Pte Ltd
                </p>
              </div>
              
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                textAlign: 'center'
              }}>
                <Users size={32} style={{ color: '#1B5E5F', marginBottom: '12px' }} />
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                  Content Provider
                </h4>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                  Microsoft Corporation
                </p>
              </div>
              
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                textAlign: 'center'
              }}>
                <Shield size={32} style={{ color: '#1B5E5F', marginBottom: '12px' }} />
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                  Content Classification
                </h4>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                  Microsoft Confidential
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section>
            <div style={{ 
              backgroundColor: '#1B5E5F',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center'
            }}>
              <h3 style={{ fontSize: '24px', fontWeight: '600', color: '#FFFFFF', marginBottom: '12px' }}>
                Ready to explore?
              </h3>
              <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.8)', marginBottom: '24px' }}>
                Access Why Microsoft – Compete Enablement for Partners to browse available competitive resources.
              </p>
              <button
                onClick={() => navigate(user ? '/browse' : '/login')}
                style={{
                  padding: '14px 32px',
                  backgroundColor: '#C4E538',
                  color: '#1B5E5F',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#B8D932'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#C4E538'}
              >
                {user ? 'Go to Why Microsoft – Compete Enablement for Partners' : 'Sign In to Access'}
              </button>
            </div>
          </section>

        </div>
      </div>

      <LevelUpFooter />
    </div>
  );
}

export default About;
